import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, BadgeCheck, Download, Loader2, Mail, MessageSquare, RefreshCw, Server, UserRound, Users } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import EmailTable from '@/components/global-admin/EmailTable';
import { useGlobalAdminStatus } from '@/hooks/useGlobalAdminStatus';
import { useSupportChat } from '@/context/SupportChatProvider';
import { authenticatedBitswanBackendInstance, getBackendHost } from '@/lib/api-client';
import { onDay, when, type SentEmailRow } from '@/lib/outbox';

type Support = {
  customer: {
    organization_id: string; name: string; plan: 'trial' | 'paid'; trial_state: string | null; days_left: number | null;
    trial_ends_at: string | null; cloud_servers: number; cloud_server_limit: number;
    invoicing_status: 'incomplete' | 'terms_pending' | 'awaiting_approval' | 'approved'; created_at: string;
  };
  billing: {
    legal_company_name: string; tax_id: string; tax_id_vies_status: string; tax_id_vies_name: string; tax_id_vies_checked_at: string | null;
    registration_number: string; address: string; contact_name: string; contact_email: string; contact_phone: string;
    currency: string; payment_terms_days: number; invoicing_status: string;
    invoice_terms_accepted_at: string | null; invoice_terms_accepted_by: string;
    approved_for_invoicing_at: string | null; approved_for_invoicing_by: string;
  } | null;
  recipients: { customer: string; advisor: string };
  advisor: { id: string; display_name: string; headline: string; contact_email: string; organization_id: string; country: string; languages: string } | null;
  contracts: Array<{ id: string; title: string; status: string; billing_mode: string; currency: string; monthly_fee: string | null; ended_at: string | null }>;
  servers: Array<{ id: string; name: string; server_type: string; hetzner_server_type: string; provisioning_status: string; ipv4_address: string | null; domain: string; created_at: string }>;
  invoices: Array<{
    id: string; invoice_number: string; billing_period: string; due_date: string; currency: string; subtotal: string; tax: string;
    tax_rate_percent: string; tax_treatment: string; total: string; status: string; paid_at: string | null;
    sent_to_customer_at: string | null; last_reminded_at: string | null; has_pdf: boolean;
    lines: Array<{ description: string; total_price: string; item_type: string }>;
  }>;
  emails: SentEmailRow[];
  members: Array<{ id?: string; email: string; username?: string; verified?: boolean }> | null;
};

const STATUS_TONE: Record<string, string> = {
  pending: 'bg-amber-500/15 text-amber-700',
  overdue: 'bg-red-500/15 text-red-700',
  paid: 'bg-emerald-500/15 text-emerald-700',
  cancelled: 'bg-muted text-muted-foreground',
};

const INVOICING: Record<string, string> = {
  awaiting_approval: 'Awaiting approval',
  approved: 'Invoice billing',
  terms_pending: 'Has not agreed yet',
  incomplete: 'No billing details',
};

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[10rem_1fr] gap-2 py-1 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words">{value || '—'}</dd>
    </div>
  );
}

export default function CustomerSupportPage() {
  const { orgId = '' } = useParams();
  const queryClient = useQueryClient();
  const { isGlobalAdmin } = useGlobalAdminStatus();
  const { openSupport } = useSupportChat();

  const support = useQuery({
    queryKey: ['global-admin-customer-support', orgId],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      return (await api.get(`/global-admin/customers/${orgId}/support/`)).data as Support;
    },
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['global-admin-customer-support', orgId] });
    queryClient.invalidateQueries({ queryKey: ['global-admin-customers'] });
  };

  const approve = useMutation({
    mutationFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      return api.post(`/global-admin/customers/${orgId}/approve/`);
    },
    onSuccess: () => { toast.success('Approved for invoice billing and emailed'); refresh(); },
    onError: (err: any) => toast.error(err.response?.data?.error || err.message),
  });

  const resend = useMutation({
    mutationFn: async (invoiceId: string) => {
      const api = await authenticatedBitswanBackendInstance();
      return api.post(`/global-admin/invoices/${invoiceId}/resend/`);
    },
    onSuccess: () => { toast.success('Invoice re-sent to the billing email'); refresh(); },
    onError: (err: any) => toast.error(err.response?.data?.error || err.message),
  });

  const markPaid = useMutation({
    mutationFn: async (invoiceId: string) => {
      const api = await authenticatedBitswanBackendInstance();
      return api.post(`/global-admin/invoices/${invoiceId}/mark-paid/`);
    },
    onSuccess: () => { toast.success('Marked paid'); refresh(); },
    onError: (err: any) => toast.error(err.response?.data?.error || err.message),
  });

  const downloadPdf = async (invoiceId: string, number: string) => {
    const api = await authenticatedBitswanBackendInstance();
    const res = await api.get(`/global-admin/invoices/${invoiceId}/pdf/`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url; a.download = `invoice_${number}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  if (support.isLoading) {
    return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }
  if (support.isError || !support.data) {
    return <p className="text-sm text-muted-foreground">Could not load this customer.</p>;
  }

  const { customer, billing, advisor, contracts, servers, invoices, emails, members } = support.data;
  const aocHost = getBackendHost(window.location.hostname).replace(/^api\./, 'aoc.');

  return (
    <div className="space-y-6" data-testid="customer-support-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/customers" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:underline">
            <ArrowLeft className="h-3 w-3" /> All customers
          </Link>
          <h2 className="mt-1 text-2xl font-bold tracking-tight">{customer.name}</h2>
          <p className="text-sm text-muted-foreground">
            {customer.plan === 'paid' ? 'Paying customer' : customer.trial_state === 'expired' ? 'Trial ended' : `Free trial, ${customer.days_left ?? '?'} days left`}
            {' · '}with BitSwan since {onDay(customer.created_at)}{' · '}
            <Badge variant="outline" className="align-middle" data-testid="support-invoicing-status">{INVOICING[customer.invoicing_status]}</Badge>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => openSupport(orgId, customer.name)} data-testid="support-message-customer">
            <MessageSquare className="mr-1 h-4 w-4" /> Message customer
          </Button>
          {advisor && (
            <Button variant="outline" onClick={() => openSupport(advisor.organization_id, advisor.display_name)} data-testid="support-message-advisor">
              <MessageSquare className="mr-1 h-4 w-4" /> Message advisor
            </Button>
          )}
          {isGlobalAdmin && customer.invoicing_status === 'awaiting_approval' && (
            <Button onClick={() => approve.mutate()} disabled={approve.isPending} data-testid="support-approve">
              {approve.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <BadgeCheck className="mr-1 h-4 w-4" />}
              Approve invoice billing
            </Button>
          )}
        </div>
      </div>

      <Card data-testid="support-advisor-card">
        <CardContent className="flex flex-wrap items-center justify-between gap-4 pt-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-500/15 text-amber-700"><UserRound className="h-5 w-5" /></div>
            <div>
              <p className="text-xs font-medium text-muted-foreground">Their advisor</p>
              {advisor ? (
                <>
                  <p className="font-semibold">{advisor.display_name}{advisor.headline ? <span className="ml-2 text-sm font-normal text-muted-foreground">{advisor.headline}</span> : null}</p>
                  <p className="text-xs text-muted-foreground">{[advisor.contact_email || 'no email on file', advisor.country, advisor.languages].filter(Boolean).join(' · ')}</p>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">None chosen yet.</p>
              )}
            </div>
          </div>
          {advisor && (
            <div className="flex flex-wrap gap-2 text-xs">
              <a className="underline text-muted-foreground" href={`${window.location.protocol}//${aocHost}/public/advisor/${advisor.id}`} target="_blank" rel="noreferrer">Public profile</a>
              <Link className="underline text-muted-foreground" to={`/advisors`}>Advisor roster</Link>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle className="text-base">Billing details</CardTitle><CardDescription>What their invoices are addressed to.</CardDescription></CardHeader>
          <CardContent>
            {billing ? (
              <dl>
                <Row label="Legal name" value={billing.legal_company_name} />
                <Row label="Address" value={billing.address} />
                <Row label="VAT ID" value={billing.tax_id ? `${billing.tax_id}${billing.tax_id_vies_status ? ` · VIES ${billing.tax_id_vies_status}${billing.tax_id_vies_name ? ` (${billing.tax_id_vies_name})` : ''}` : ''}` : ''} />
                <Row label="Registration no." value={billing.registration_number} />
                <Row label="Contact" value={`${billing.contact_name} · ${billing.contact_email} · ${billing.contact_phone}`} />
                <Row label="Currency" value={billing.currency || 'platform default'} />
                <Row label="Payment terms" value={`${billing.payment_terms_days} days`} />
                <Row label="Agreed to invoices" value={billing.invoice_terms_accepted_at ? `${when(billing.invoice_terms_accepted_at)} by ${billing.invoice_terms_accepted_by || 'an admin'}` : 'not yet'} />
                <Row label="Approved" value={billing.approved_for_invoicing_at ? `${when(billing.approved_for_invoicing_at)} by ${billing.approved_for_invoicing_by || '—'}` : 'not yet'} />
              </dl>
            ) : <p className="text-sm text-muted-foreground">No billing details entered.</p>}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Account</CardTitle><CardDescription>Servers, people and their advisor.</CardDescription></CardHeader>
          <CardContent className="space-y-4">
            <dl>
              <Row label="Cloud servers" value={`${customer.cloud_servers} of ${customer.cloud_server_limit} allowed`} />
              <Row label="Organisation id" value={<code className="text-xs">{customer.organization_id}</code>} />
            </dl>
            <div>
              <p className="mb-1 flex items-center gap-1 text-xs font-medium text-muted-foreground"><Server className="h-3.5 w-3.5" /> Servers</p>
              {servers.length === 0 ? <p className="text-sm text-muted-foreground">None.</p> : (
                <ul className="space-y-1 text-sm">
                  {servers.map((s) => (
                    <li key={s.id} className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{s.name}</span>
                      <Badge variant="outline" className="font-normal">{s.server_type === 'hetzner' ? `cloud ${s.hetzner_server_type || ''}`.trim() : 'their own'}</Badge>
                      <span className="text-xs text-muted-foreground">{s.provisioning_status}{s.domain ? ` · ${s.domain}` : ''}{s.ipv4_address ? ` · ${s.ipv4_address}` : ''} · since {onDay(s.created_at)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <p className="mb-1 flex items-center gap-1 text-xs font-medium text-muted-foreground"><Users className="h-3.5 w-3.5" /> People</p>
              {members === null ? <p className="text-sm text-muted-foreground">Keycloak did not answer.</p>
                : members.length === 0 ? <p className="text-sm text-muted-foreground">Nobody yet.</p>
                : <p className="text-sm">{members.map((m) => m.email).filter(Boolean).join(', ')}</p>}
            </div>
            {contracts.length > 0 && (
              <div>
                <p className="mb-1 text-xs font-medium text-muted-foreground">Contracts with their advisor</p>
                <ul className="space-y-1 text-sm">
                  {contracts.map((c) => (
                    <li key={c.id} className="flex flex-wrap items-center gap-2">
                      <span>{c.title}</span>
                      <Badge variant="outline" className="font-normal">{c.ended_at ? 'ended' : c.status}</Badge>
                      <span className="text-xs text-muted-foreground">{c.monthly_fee ? `${c.monthly_fee} ${c.currency} a month · ` : ''}{c.billing_mode === 'via_aoc' ? 'billed through BitSwan' : 'billed by the advisor'}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Invoices</CardTitle><CardDescription>Every invoice issued to them, when it was sent and last chased.</CardDescription></CardHeader>
        <CardContent>
          {invoices.length === 0 ? <p className="text-sm text-muted-foreground">No invoices yet.</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-muted-foreground">
                  <tr><th className="py-1">Invoice</th><th className="py-1">Period</th><th className="py-1">Due</th><th className="py-1 text-right">Net</th><th className="py-1 text-right">VAT</th><th className="py-1 text-right">Total</th><th className="py-1">Status</th><th className="py-1">Sent</th><th className="py-1">Last reminder</th><th /></tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv.id} className="border-t align-top" data-testid={`support-invoice-${inv.invoice_number}`}>
                      <td className="py-2 font-medium">{inv.invoice_number}<div className="text-xs font-normal text-muted-foreground">{inv.lines.map((l) => l.description).join(' · ')}</div></td>
                      <td className="py-2">{inv.billing_period}</td>
                      <td className="py-2">{onDay(inv.due_date)}</td>
                      <td className="py-2 text-right text-muted-foreground">{inv.subtotal}</td>
                      <td className="py-2 text-right text-muted-foreground">{Number(inv.tax_rate_percent) > 0 ? `${inv.tax} (${Number(inv.tax_rate_percent)}%)` : inv.tax_treatment.replace('_', ' ')}</td>
                      <td className="py-2 text-right">{inv.total} {inv.currency}</td>
                      <td className="py-2"><Badge className={STATUS_TONE[inv.status] ?? ''}>{inv.status}</Badge>{inv.paid_at && <div className="text-xs text-muted-foreground">{onDay(inv.paid_at)}</div>}</td>
                      <td className="py-2 text-xs text-muted-foreground">{inv.sent_to_customer_at ? when(inv.sent_to_customer_at) : 'never'}</td>
                      <td className="py-2 text-xs text-muted-foreground">{inv.last_reminded_at ? when(inv.last_reminded_at) : '—'}</td>
                      <td className="py-2 text-right whitespace-nowrap">
                        {inv.has_pdf && (
                          <Button size="sm" variant="ghost" onClick={() => downloadPdf(inv.id, inv.invoice_number)} title="Download PDF"><Download className="h-3.5 w-3.5" /></Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => resend.mutate(inv.id)} disabled={resend.isPending} title="Re-send to the billing email" data-testid={`support-resend-${inv.invoice_number}`}><Mail className="h-3.5 w-3.5" /></Button>
                        {isGlobalAdmin && (inv.status === 'pending' || inv.status === 'overdue') && (
                          <Button size="sm" variant="outline" className="ml-1" onClick={() => markPaid.mutate(inv.id)} disabled={markPaid.isPending}>Mark paid</Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div><CardTitle className="text-base">Emails to this customer</CardTitle><CardDescription>Everything the platform sent them or their billing address.</CardDescription></div>
            <Button size="sm" variant="ghost" onClick={refresh}><RefreshCw className="mr-1 h-3.5 w-3.5" /> Refresh</Button>
          </div>
        </CardHeader>
        <CardContent><EmailTable emails={emails} showCustomer={false} /></CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        The customer sees their side at <a className="underline" href={`${window.location.protocol}//${aocHost}/billing`} target="_blank" rel="noreferrer">{aocHost}/billing</a> once switched into this organisation.
      </p>
    </div>
  );
}
